<ts-dialog suppressHeader="true">
  <div class="flex flex-row justify-center items-center w-full gap-4" ts-dialog-body>
    <div class="w-2/3 m-10 h-128">
      <ts-panel [title]="panelFormControls.title.value || '-'" [headerColor]="this.panelFormControls.headerColor.value">
        <!--    Height must be fixed for the charts -->
        <section ts-panel-content class="h-128 overflow-y-auto">
          @if (panelForm.valid && visualizationFormControls.field.value?.length > 0 && !loadingFields()) {
            @if (previewChartData | async; as previewChartData) {
              @if (previewChartData[0] && previewChartData[1]) {
                <div class="p-8 h-full">
                  <chart-renderer
                    [loading]="loadingPreview()"
                    [data]="{ visualizationConfig: previewChartData[0], dataSource: previewChartData[1] }"
                  />
                </div>
              } @else {
                <ng-container *ngTemplateOutlet="emptyState"></ng-container>
              }
            } @else {
              <ng-container *ngTemplateOutlet="emptyState"></ng-container>
            }
          } @else {
            <ng-container *ngTemplateOutlet="emptyState"></ng-container>
          }
        </section>
      </ts-panel>
    </div>
    <div class="w-1/3 h-full self-start p-4 flex flex-col gap-4 border border-black-200 rounded">
      <!-- STEP 1: Panel Configuration -->
      <form class="flex flex-col gap-4" [formGroup]="panelForm">
        <p class="text-2xl font-bold">Panel Configuration</p>
        <div class="w-full flex flex-col gap-2 items-center">
          <ts-input
            placeholder="Panel Title"
            class="w-full"
            type="text"
            [formControl]="panelFormControls.title"
            [roundedBorders]="false"
          ></ts-input>
          <!-- TODO: create an ng-template reusable thing -->
          <ts-select
            [items]="accountColorOptions$ | async"
            itemKey="name"
            itemLabel="label"
            class="w-full"
            formControlName="headerColor"
            [uiOptions]="{
              roundedBorders: false,
              placeholder: 'Header Color',
              searchPlaceholder: 'Search for a Panel Type...',
              size: 'small',
            }"
          />

          <ts-select
            [items]="relatedToOptions"
            itemKey="value"
            itemLabel="label"
            class="w-full"
            [formControl]="relatedToForm"
            [uiOptions]="{
              roundedBorders: false,
              placeholder: 'Related To',
              searchPlaceholder: 'Search for a related to...',
              size: 'small',
            }"
          >
          </ts-select>

          <ts-select
            [items]="visualizationOptions"
            itemKey="value"
            itemLabel="label"
            class="w-full"
            formControlName="type"
            [uiOptions]="{
              roundedBorders: false,
              placeholder: 'Visualization Type',
              searchPlaceholder: 'Search for a Visualization Type...',
              size: 'small',
            }"
          >
          </ts-select>

          <ts-select
            itemKey="key"
            itemLabel="label"
            class="w-full"
            [formControl]="panelFormControls.reportPeriod"
            [hasGroups]="false"
            [uiOptions]="{
              roundedBorders: false,
              placeholder: 'Select a period',
              searchPlaceholder: 'Select period...',
              size: 'full',
            }"
            [items]="reportPeriodOptions"
          ></ts-select>
        </div>
      </form>
      <!-- STEP 2: Visualization Settings -->
      @if (loadingFields()) {
        <div class="flex flex-col w-full gap-2">
          <ts-skeleton />
          <ts-skeleton />
          <ts-skeleton />
          <ts-skeleton />
        </div>
      } @else {
        @if (enableVisualizationSettings) {
          <ts-divider spacing="sm" />
          <form class="w-full flex flex-col gap-2 items-center" [formGroup]="visualizationForm">
            <div class="h-ts-field-height w-full flex flex-row items-center">
              <ts-select
                itemKey="value"
                itemLabel="label"
                class="w-full"
                [multi]="true"
                [formControl]="visualizationFormControls.field"
                [hasGroups]="false"
                [uiOptions]="{
                  roundedBorders: false,
                  placeholder: 'Select some fields',
                  searchPlaceholder: 'Search fields...',
                  size: 'small',
                }"
                [items]="fieldOptions | async"
              ></ts-select>
            </div>

            <!-- STEP 3: Specific Field Settings -->
            @if (fieldsForm.controls.length > 0 && panelForm.valid) {
              <div class="flex flex-col items-center w-full gap-1">
                @for (control of fieldsFormControls; track control.value.fieldId; let i = $index) {
                  <form
                    class="w-full flex flex-row justify-between items-center text-sm border border-black-200 rounded px-1 py-1 gap-2"
                    [formGroup]="control"
                  >
                    <div class="w-full flex flex-row items-center gap-1">
                      <ts-color-bubble size="lg" [hexColor]="control?.value?.color">
                        <div class="flex flex-row">
                          <ts-button
                            color="accent"
                            icon="ts_palette"
                            type="icon"
                            (click)="openColorPicker(i, control?.value?.color)"
                          ></ts-button>
                          <input
                            colorPicker
                            cpAlphaChannel="disabled"
                            class="w-0"
                            (colorPickerSelect)="onFieldColorSelected($event, control?.value?.color, i)"
                          />
                        </div>
                      </ts-color-bubble>

                      @if (fieldOptionsLabelMap | async; as fieldOptionsLabelMap) {
                        <p
                          [ngClass]="panelFormControls.type.value === 'mixed' ? 'max-w-32' : 'max-w-50'"
                          class="font-semibold truncate"
                          [matTooltip]="fieldOptionsLabelMap[control.value.fieldId]"
                        >
                          @if (fieldOptionsLabelMap[control.value.fieldId]) {
                            {{ fieldOptionsLabelMap[control.value.fieldId] }}
                          } @else {
                            <span class="text-cool-gray-300 italic font-normal">Field not found</span>
                          }
                        </p>
                      }
                    </div>

                    <div class="flex flex-row gap-2" [ngStyle]="{ zoom: '80%' }">
                      @if (panelFormControls.type.value === 'mixed') {
                        <mat-button-toggle-group [formControl]="control.get('type')">
                          <mat-button-toggle value="line">Line</mat-button-toggle>
                          <mat-button-toggle value="column">Column</mat-button-toggle>
                        </mat-button-toggle-group>
                      }

                      <div class="flex flex-row items-center gap-1">
                        <p>Label</p>
                        <mat-slide-toggle [formControl]="control.get('displayLabels')" />
                      </div>
                    </div>
                  </form>
                }
              </div>

              <ts-divider spacing="sm" />

              @if (panelFormControls.type.value !== 'mixed') {
                <div class="h-ts-field-height w-full flex flex-row items-center justify-between">
                  <p class="flex flex-row items-center gap-1">Direction</p>
                  <mat-button-toggle-group
                    class="rounded-none w-50"
                    [formControl]="visualizationFormControls.direction"
                  >
                    <mat-button-toggle class="w-full" value="vertical">Vertical</mat-button-toggle>
                    <mat-button-toggle class="w-full" value="horizontal">Horizontal</mat-button-toggle>
                  </mat-button-toggle-group>
                </div>
              }

              <div class="h-ts-field-height w-full flex flex-row items-center justify-between">
                <p class="flex flex-row items-center gap-1">Reverse</p>
                <mat-slide-toggle [formControl]="visualizationFormControls.reversed" />
              </div>

              @if (panelFormControls.type.value === 'bar') {
                <div class="h-ts-field-height w-full flex flex-row items-center justify-between">
                  <p class="flex flex-row items-center gap-1">Stacked</p>
                  <mat-slide-toggle [formControl]="visualizationFormControls.stacked" />
                </div>
              }
            }
          </form>
        }
      }
    </div>
  </div>

  <div class="ts-dialog-actions-container" ts-dialog-actions>
    <ts-button variant="secondary" (click)="closeDialog()">Cancel</ts-button>
    <ts-button
      variant="primary"
      [disabled]="panelForm.invalid || visualizationForm.invalid || fieldsForm.controls.length === 0"
      (click)="createPanel()"
      >Save</ts-button
    >
  </div>
</ts-dialog>

<ng-template #emptyState>
  <ts-empty-state
    icon="ts_graph-bar"
    title="Preview Unavailable"
    subtitle="Keep editing the panel's configuration"
  ></ts-empty-state>
</ng-template>
